import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateImageLibraryQuery } from '../ImageLibrary/useImageLibraryQuery';

/** What the server accepts, kept here so the file dialog offers exactly the same set. */
export const LIBRARY_FILE_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.txt,.md';

export const LIBRARY_FILE_MAX_BYTES = 20_000_000;

/**
 * Uploads reference documents into the account's library. Unlike an image upload, joining the library is
 * the whole point rather than a side effect, so the endpoint reports a file it could not file as a failure.
 */
export function useUploadLibraryFilesMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: Api.IBulkOperationResult<Api.IFileAttachmentWithURL>) => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({
			files,
			impersonationContext,
		}: {
			files: File[];
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			const formData = new FormData();
			files.forEach(file => formData.append('files', file));

			return new Promise<Api.IBulkOperationResult<Api.IFileAttachmentWithURL>>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceWithBulkOperationResult<Api.IFileAttachmentWithURL>(
					Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: 'attachment/libraryFile' }),
					'POST',
					formData,
					resolve,
					reject
				);
			});
		},
		onError,
		onSuccess: result => {
			invalidateImageLibraryQuery();
			onSuccess?.(result);
		},
	});
}
